import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate, useParams } from 'react-router-dom';
import {
  addAdminOutreachSuppression,
  fetchAdminOutreachCampaigns,
  fetchAdminOutreachCampaignStats,
  fetchAdminOutreachMessage,
  fetchAdminOutreachMessages,
  fetchAdminOutreachSuppressions,
  sendAdminOutreachMessage,
  updateAdminOutreachMessage,
} from '../token/tokenIntelligenceApi';

// Every text-bearing rule below sets textAlign: 'left' explicitly, and no
// container uses auto margins to center itself — standing preference, not
// specific to this page: left-align everything, never center.
const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: "'Inter', sans-serif",
    color: '#ffffff',
    padding: '26px 18px 48px',
    maxWidth: 1400,
    width: '100%',
    boxSizing: 'border-box',
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: { padding: '14px 12px 32px' },
  },
  panel: {
    padding: '22px 24px',
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.35)',
    background: 'radial-gradient(circle at 18% 18%, rgba(77,121,255,0.16), rgba(10,14,30,0.96))',
    textAlign: 'left',
  },
  title: {
    fontSize: '1.45rem',
    fontWeight: 900,
    color: '#e5e7ff',
    marginBottom: 8,
    textAlign: 'left',
  },
  muted: {
    color: '#9ea4d8',
    fontSize: 13,
    lineHeight: 1.55,
    textAlign: 'left',
  },
  body: {
    color: '#d5dcff',
    fontSize: 13,
    lineHeight: 1.55,
    textAlign: 'left',
  },
  controls: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 18,
  },
  textField: {
    textAlign: 'left',
    '& .MuiOutlinedInput-input': { color: '#fff', textAlign: 'left' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.22)' },
    '& .MuiInputLabel-root': { color: '#9ea4d8' },
  },
  // Vertically scrolling list, one row per candidate/message — bounded
  // height so it scrolls up/down once there are more than fit on screen,
  // rather than growing the page indefinitely or scrolling sideways.
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    marginTop: 16,
    maxHeight: 420,
    overflowY: 'auto',
    paddingRight: 4,
  },
  // CSS Grid, not flexbox, for the row — every row shares the exact same
  // fixed column tracks (icon/confidence/status/channel), so those columns
  // line up across rows regardless of how long any one row's token name or
  // badge text is. Flexbox couldn't do this with only one flexible column:
  // when the trailing badges (status/channel) vary in width per row, the
  // flexible name column absorbs the difference, which shifts every column
  // after it out of alignment — the grid's fixed tracks don't have that
  // problem since they never resize based on content.
  listRow: {
    display: 'grid',
    gridTemplateColumns: '26px 1fr 110px 100px 100px',
    alignItems: 'center',
    justifyItems: 'start',
    gap: 12,
    padding: '10px 14px',
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.04)',
    cursor: 'pointer',
    textAlign: 'left',
  },
  listRowActive: {
    borderColor: 'rgba(118,255,184,0.38)',
    background: 'rgba(118,255,184,0.08)',
  },
  listRowName: {
    minWidth: 0,
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  listRowConfidence: {
    width: '100%',
  },
  tokenIcon: {
    flexShrink: 0,
    width: 26,
    height: 26,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    fontSize: 12,
    fontWeight: 700,
    color: '#d5dcff',
  },
  tokenIconImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  badgeRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  badge: {
    borderRadius: 999,
    border: '1px solid rgba(77,121,255,0.3)',
    background: 'rgba(77,121,255,0.12)',
    color: '#d5dcff',
    fontSize: 12,
    padding: '4px 10px',
    textAlign: 'left',
  },
  statusDraft: { borderColor: 'rgba(255, 214, 118, 0.4)', background: 'rgba(255, 214, 118, 0.12)' },
  statusSent: { borderColor: 'rgba(118,255,184,0.4)', background: 'rgba(118,255,184,0.12)' },
  statusBlocked: { borderColor: 'rgba(255,154,154,0.4)', background: 'rgba(255,154,154,0.12)' },
  section: {
    marginTop: 16,
    padding: 14,
    borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.04)',
    textAlign: 'left',
  },
  sectionLabel: {
    color: '#9ea4d8',
    fontSize: 11,
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginBottom: 10,
    textAlign: 'left',
  },
  error: {
    color: '#ff9a9a',
    fontSize: 13,
    marginTop: 12,
    textAlign: 'left',
  },
  success: {
    color: '#76ffb8',
    fontSize: 13,
    marginTop: 12,
    textAlign: 'left',
  },
}));

function formatConfidence(value) {
  // Number(null) is 0, not NaN — without this check a message drafted
  // before outreachConfidence existed (null) would misleadingly show
  // "0.00" (implying zero confidence) instead of "unknown".
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : '—';
}

function formatDate(value) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function statusClass(classes, status) {
  if (status === 'draft') return classes.statusDraft;
  if (status === 'sent' || status === 'delivered' || status === 'opened' || status === 'clicked') return classes.statusSent;
  if (status === 'blocked' || status === 'bounced' || status === 'complained') return classes.statusBlocked;
  return '';
}

// Telegram/X can't be sent programmatically for cold outreach — Telegram's
// Bot API can't initiate a DM to someone who hasn't messaged the bot first,
// and X's API terms prohibit automating unsolicited DMs (real risk of the
// sending account getting suspended). So for those two channels this is
// deliberately "help a human send it themselves fast," not automation:
// open the actual chat/profile (recipientValueEncrypted already holds the
// real t.me/x.com URL, despite the misleading field name — it's not
// actually encrypted) and let them paste the copied text. Email still sends
// automatically via SES elsewhere; the mailto: link here is just a
// convenience for previewing/sending from the admin's own mail client.
function buildComposeLink(message) {
  const value = message && message.recipientValueEncrypted ? message.recipientValueEncrypted : '';
  if (!value) return '';
  if (message.channel === 'telegram' || message.channel === 'x') return value;
  if (message.channel === 'email') {
    const subject = encodeURIComponent(message.subject || '');
    const body = encodeURIComponent(message.body || '');
    return `mailto:${value}?subject=${subject}&body=${body}`;
  }
  return '';
}

function composeLinkLabel(channel) {
  if (channel === 'telegram') return 'Open Telegram chat';
  if (channel === 'x') return 'Open X profile';
  if (channel === 'email') return 'Open in mail client';
  return 'Open';
}

export default function ProjectOutreachManagerPage() {
  const classes = useStyles();
  const navigate = useNavigate();
  const { messageId } = useParams();
  // No client-side auth gating here on purpose — matches
  // ProjectDiscoveryReviewPage/ProjectClaimReviewPage, neither of which
  // reads auth state either. Two earlier attempts at a client-side
  // pre-check both broke: gating on auth.authChecked hung indefinitely
  // (that flag only flips once a background /auth/status call resolves,
  // and that call can hang). Gating on auth.isAuthenticated instead broke
  // a different way — AuthContext's background verification calls
  // logout() whenever that specific check fails (client/src/auth/
  // AuthContext.js:207-241), which force-clears isAuthenticated even when
  // the real admin session is still perfectly valid server-side. Both
  // symptoms were the same underlying shape: client-side "am I logged in"
  // state can be wrong or delayed in ways the real server-side check
  // (requireAdminJson, already correct) isn't. So: always fire the
  // requests, and let the server's actual 401/403 — surfaced as
  // ADMIN_ACCESS_REQUIRED below — be the only source of truth for showing
  // "please log in" vs "admin access required" vs real content.
  const [statusFilter, setStatusFilter] = useState('draft');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [listState, setListState] = useState({ loading: true, error: '', data: null });
  const [detailState, setDetailState] = useState({ loading: false, error: '', data: null });
  const [accessDenied, setAccessDenied] = useState('');

  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [sendStatus, setSendStatus] = useState('');
  const [copyStatus, setCopyStatus] = useState('');

  const [campaignStatsState, setCampaignStatsState] = useState({ loading: false, error: '', data: null });
  const [suppressionsExpanded, setSuppressionsExpanded] = useState(false);
  const [suppressions, setSuppressions] = useState([]);
  const [suppressionsLoading, setSuppressionsLoading] = useState(false);
  const [suppressReason, setSuppressReason] = useState('');
  const [suppressing, setSuppressing] = useState(false);
  const [suppressError, setSuppressError] = useState('');
  const [suppressStatus, setSuppressStatus] = useState('');

  useEffect(() => {
    fetchAdminOutreachCampaigns({}).then((result) => {
      setCampaigns(result && Array.isArray(result.campaigns) ? result.campaigns : []);
    }).catch((error) => {
      if (error && error.code === 'ADMIN_ACCESS_REQUIRED') setAccessDenied(error.message);
    });
  }, []);

  useEffect(() => {
    if (!campaignFilter) {
      setCampaignStatsState({ loading: false, error: '', data: null });
      return undefined;
    }
    let active = true;
    setCampaignStatsState({ loading: true, error: '', data: null });
    fetchAdminOutreachCampaignStats(campaignFilter).then((result) => {
      if (!active) return;
      setCampaignStatsState({ loading: false, error: '', data: result });
    }).catch((error) => {
      if (!active) return;
      setCampaignStatsState({ loading: false, error: error && error.message ? error.message : 'Failed to load campaign stats', data: null });
    });
    return () => { active = false; };
  }, [campaignFilter]);

  const loadSuppressions = () => {
    setSuppressionsLoading(true);
    fetchAdminOutreachSuppressions({ limit: 100 }).then((result) => {
      setSuppressions(result && Array.isArray(result.suppressions) ? result.suppressions : []);
    }).catch(() => null).finally(() => setSuppressionsLoading(false));
  };

  useEffect(() => {
    if (suppressionsExpanded) loadSuppressions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suppressionsExpanded]);

  useEffect(() => {
    let active = true;
    setListState({ loading: true, error: '', data: null });
    fetchAdminOutreachMessages({
      status: statusFilter,
      campaignId: campaignFilter,
      limit: 100,
    }).then((result) => {
      if (!active) return;
      setListState({ loading: false, error: '', data: result });
      const messages = result && Array.isArray(result.messages) ? result.messages : [];
      if (!messageId && messages[0] && messages[0].messageId) {
        navigate(`/admin/outreach/${encodeURIComponent(messages[0].messageId)}`, { replace: true });
      }
    }).catch((error) => {
      if (!active) return;
      if (error && error.code === 'ADMIN_ACCESS_REQUIRED') {
        setAccessDenied(error.message);
        setListState({ loading: false, error: '', data: null });
        return;
      }
      setListState({ loading: false, error: error && error.message ? error.message : 'Failed to load messages', data: null });
    });
    return () => { active = false; };
  }, [statusFilter, campaignFilter, messageId, navigate]);

  useEffect(() => {
    if (!messageId) return undefined;
    let active = true;
    setDetailState({ loading: true, error: '', data: null });
    setSaveStatus('');
    setSaveError('');
    setSendStatus('');
    setSendError('');
    fetchAdminOutreachMessage(messageId).then((result) => {
      if (!active) return;
      setDetailState({ loading: false, error: '', data: result });
      const message = result && result.message ? result.message : null;
      setEditSubject(message && message.subject ? message.subject : '');
      setEditBody(message && message.body ? message.body : '');
    }).catch((error) => {
      if (!active) return;
      if (error && error.code === 'ADMIN_ACCESS_REQUIRED') {
        setAccessDenied(error.message);
        setDetailState({ loading: false, error: '', data: null });
        return;
      }
      setDetailState({ loading: false, error: error && error.message ? error.message : 'Failed to load message', data: null });
    });
    return () => { active = false; };
  }, [messageId]);

  const messages = useMemo(() => (listState.data && Array.isArray(listState.data.messages) ? listState.data.messages : []), [listState.data]);
  const selected = detailState.data && detailState.data.message ? detailState.data.message : null;
  const isDraft = selected && selected.status === 'draft';

  const handleSaveEdit = async () => {
    if (!messageId) return;
    setSaving(true);
    setSaveError('');
    setSaveStatus('');
    try {
      const result = await updateAdminOutreachMessage(messageId, { subject: editSubject, body: editBody });
      setDetailState({ loading: false, error: '', data: result });
      setSaveStatus('Draft updated.');
    } catch (error) {
      setSaveError(error && error.message ? error.message : 'Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!messageId) return;
    setSending(true);
    setSendError('');
    setSendStatus('');
    try {
      // Save any unsaved edits first so what gets sent matches what's on screen.
      await updateAdminOutreachMessage(messageId, { subject: editSubject, body: editBody });
      const result = await sendAdminOutreachMessage(messageId, {});
      setDetailState({ loading: false, error: '', data: { message: result.message } });
      setSendStatus(result && result.blocked
        ? `Send attempted but blocked (status: ${result.message.status}). Check contact approval, campaign legal approval/provider, and PROJECT_OUTREACH_ENABLED.`
        : `Sent (status: ${result.message.status}).`);
    } catch (error) {
      setSendError(error && error.message ? error.message : 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleCopy = async () => {
    setCopyStatus('');
    const text = selected && selected.channel === 'email' ? `Subject: ${editSubject}\n\n${editBody}` : editBody;
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus('Copied to clipboard.');
    } catch (error) {
      setCopyStatus('Could not copy automatically — select and copy the text above manually.');
    }
  };

  // Manual fallback for the case a one-click List-Unsubscribe link can't
  // cover: a recipient just hits reply and asks in text instead. Available
  // at any message status, not just draft — this is about the recipient's
  // preference, not the message's lifecycle.
  const handleSuppressContact = async () => {
    if (!selected || !selected.recipientValueEncrypted) return;
    setSuppressing(true);
    setSuppressError('');
    setSuppressStatus('');
    try {
      await addAdminOutreachSuppression({
        type: selected.channel === 'email' ? 'email' : selected.channel,
        value: selected.recipientValueEncrypted,
        reason: suppressReason || 'Manually suppressed from outreach admin',
      });
      setSuppressStatus('Contact suppressed — future outreach to this contact will be blocked.');
      if (suppressionsExpanded) loadSuppressions();
    } catch (error) {
      setSuppressError(error && error.message ? error.message : 'Failed to suppress contact');
    } finally {
      setSuppressing(false);
    }
  };

  // accessDenied is driven entirely by the server's real 401/403 response
  // (throwAdminOutreachError in tokenIntelligenceApi.js already picks the
  // right wording — "Please log in..." for 401, "Admin access required."
  // for 403) — no separate client-side "not logged in" branch needed.
  if (accessDenied) {
    return (
      <Box className={classes.root}>
        <Box className={classes.panel} style={{ maxWidth: 520 }}>
          <Typography className={classes.title}>Access required</Typography>
          <Typography className={classes.muted}>{accessDenied}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.panel}>
        <Typography className={classes.title}>Outreach Messages</Typography>
        <Typography className={classes.muted}>
          Draft, edit, and send project outreach messages. Sending still requires the target contact to be manually approved (Project Discovery Review), the campaign to be legally approved with a provider, and PROJECT_OUTREACH_ENABLED=1 — Telegram/X are manual-only regardless.
        </Typography>

        <Box className={classes.controls}>
          <TextField
            select
            variant="outlined"
            label="Status"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className={classes.textField}
            style={{ minWidth: 160 }}
          >
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="scheduled">Scheduled</MenuItem>
            <MenuItem value="sent">Sent</MenuItem>
            <MenuItem value="blocked">Blocked</MenuItem>
            <MenuItem value="delivered">Delivered</MenuItem>
            <MenuItem value="bounced">Bounced</MenuItem>
            <MenuItem value="">All</MenuItem>
          </TextField>
          <TextField
            select
            variant="outlined"
            label="Campaign"
            value={campaignFilter}
            onChange={(event) => setCampaignFilter(event.target.value)}
            className={classes.textField}
            style={{ minWidth: 220 }}
          >
            <MenuItem value="">All campaigns</MenuItem>
            {campaigns.map((c) => (
              <MenuItem key={c.campaignId} value={c.campaignId}>{c.name || c.campaignId}</MenuItem>
            ))}
          </TextField>
        </Box>

        {campaignFilter && campaignStatsState.loading ? <Typography className={classes.body} style={{ marginTop: 18 }}>Loading campaign stats…</Typography> : null}
        {campaignFilter && campaignStatsState.error ? <Typography className={classes.error}>{campaignStatsState.error}</Typography> : null}
        {campaignFilter && !campaignStatsState.loading && campaignStatsState.data ? (
          <Box className={classes.section} style={{ marginTop: 18 }}>
            <Typography className={classes.sectionLabel}>Campaign Health</Typography>
            {campaignStatsState.data.campaign.status === 'paused' ? (
              <Box className={`${classes.badge} ${classes.statusBlocked}`} style={{ display: 'inline-block', marginBottom: 10 }}>
                PAUSED — {campaignStatsState.data.campaign.pauseReason || 'unknown reason'}
              </Box>
            ) : null}
            <Box className={classes.badgeRow}>
              {Object.entries(campaignStatsState.data.messagesByStatus || {}).map(([status, count]) => (
                <Box key={status} className={`${classes.badge} ${statusClass(classes, status)}`}>{status}: {count}</Box>
              ))}
              {!Object.keys(campaignStatsState.data.messagesByStatus || {}).length ? (
                <Box className={classes.badge}>No messages yet</Box>
              ) : null}
            </Box>
            <Typography className={classes.muted} style={{ marginTop: 10 }}>
              Bounce rate {(campaignStatsState.data.bounceRate * 100).toFixed(1)}% (pauses at {(campaignStatsState.data.bounceRatePauseThreshold * 100).toFixed(0)}%) ·
              {' '}Complaints {campaignStatsState.data.complaintCount} (pauses at {campaignStatsState.data.complaintCountPauseThreshold}) ·
              {' '}Legal approved: {campaignStatsState.data.campaign.legalApproved ? 'yes' : 'no'} · Provider: {campaignStatsState.data.campaign.provider || 'none'}
            </Typography>
          </Box>
        ) : null}

        <Box className={classes.section} style={{ marginTop: 18 }}>
          <Box
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
            onClick={() => setSuppressionsExpanded((prev) => !prev)}
          >
            <Typography className={classes.sectionLabel} style={{ marginBottom: 0 }}>
              Suppressed Contacts {suppressionsExpanded ? '▾' : '▸'}
            </Typography>
          </Box>
          {suppressionsExpanded ? (
            <>
              {suppressionsLoading ? <Typography className={classes.body} style={{ marginTop: 10 }}>Loading…</Typography> : null}
              {!suppressionsLoading && !suppressions.length ? <Typography className={classes.muted} style={{ marginTop: 10 }}>No suppressed contacts.</Typography> : null}
              <Box className={classes.list} style={{ maxHeight: 260 }}>
                {suppressions.map((item) => (
                  <Box key={item.suppressionId} style={{ padding: '8px 4px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    <Typography className={classes.body}>{item.type}: {item.value || '(hashed only)'}</Typography>
                    <Typography className={classes.muted}>{item.reason || 'No reason given'} · {item.source} · {formatDate(item.createdAt)}</Typography>
                  </Box>
                ))}
              </Box>
            </>
          ) : null}
        </Box>

        {listState.loading ? <Typography className={classes.body} style={{ marginTop: 18 }}>Loading messages…</Typography> : null}
        {listState.error ? <Typography className={classes.error}>{listState.error}</Typography> : null}
        {!listState.loading && !listState.error && !messages.length ? (
          <Typography className={classes.muted} style={{ marginTop: 18 }}>
            No messages match this filter. Generate drafts with: node generateOutreachDrafts.js
          </Typography>
        ) : null}

        <Box className={classes.list}>
          {messages.map((message) => {
            const active = messageId === message.messageId;
            const initial = (message.tokenSymbol || message.tokenName || message.tokenAddress || '?').slice(0, 1).toUpperCase();
            return (
              <Box
                key={message.messageId}
                className={`${classes.listRow} ${active ? classes.listRowActive : ''}`}
                onClick={() => navigate(`/admin/outreach/${encodeURIComponent(message.messageId)}`)}
              >
                <Box className={classes.tokenIcon}>
                  {message.tokenIcon ? (
                    <img className={classes.tokenIconImg} src={message.tokenIcon} alt="" />
                  ) : initial}
                </Box>
                <Typography className={`${classes.body} ${classes.listRowName}`} style={{ fontWeight: 700 }}>
                  {message.tokenName || message.tokenAddress}{message.tokenSymbol ? ` (${message.tokenSymbol})` : ''}
                </Typography>
                <Typography className={`${classes.muted} ${classes.listRowConfidence}`}>
                  Confidence {formatConfidence(message.outreachConfidence)}
                </Typography>
                <Box className={`${classes.badge} ${statusClass(classes, message.status)}`}>{message.status}</Box>
                <Box className={classes.badge}>{message.channel}</Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box className={classes.panel} style={{ marginTop: 18 }}>
        <Typography className={classes.title}>Message</Typography>

        {detailState.loading ? <Typography className={classes.body} style={{ marginTop: 18 }}>Loading…</Typography> : null}
        {detailState.error ? <Typography className={classes.error}>{detailState.error}</Typography> : null}

        {!detailState.loading && selected ? (
          <>
            <Box className={classes.section}>
              <Typography className={classes.sectionLabel}>Recipient</Typography>
              <Typography className={classes.body}>{selected.channel}: {selected.recipientHandle || selected.recipientDomain || selected.recipientValueEncrypted || '(hashed — original value not shown)'}</Typography>
              <Box className={classes.badgeRow}>
                <Box className={`${classes.badge} ${statusClass(classes, selected.status)}`}>{selected.status}</Box>
                <Box className={classes.badge}>
                  {selected.tokenName || selected.tokenAddress}{selected.tokenSymbol ? ` (${selected.tokenSymbol})` : ''}
                </Box>
                <Box className={classes.badge}>confidence {formatConfidence(selected.outreachConfidence)}</Box>
                <Box className={classes.badge}>campaign {selected.campaignId}</Box>
              </Box>
              <Typography className={classes.muted} style={{ marginTop: 10 }}>
                Created {formatDate(selected.createdAt)}{selected.sentAt ? ` · Sent ${formatDate(selected.sentAt)}` : ''}
              </Typography>
            </Box>

            <Box className={classes.section}>
              <Typography className={classes.sectionLabel}>Content</Typography>
              <TextField
                fullWidth
                variant="outlined"
                label="Subject"
                value={editSubject}
                onChange={(event) => setEditSubject(event.target.value)}
                className={classes.textField}
                disabled={!isDraft}
                style={{ marginBottom: 12 }}
              />
              <TextField
                fullWidth
                multiline
                minRows={8}
                variant="outlined"
                label="Body"
                value={editBody}
                onChange={(event) => setEditBody(event.target.value)}
                className={classes.textField}
                disabled={!isDraft}
              />
              {!isDraft ? (
                <Typography className={classes.muted} style={{ marginTop: 10 }}>
                  Only draft messages can be edited — this one is already {selected.status}, kept as-is for the record.
                </Typography>
              ) : null}

              {selected.channel === 'telegram' || selected.channel === 'x' ? (
                <Typography className={classes.muted} style={{ marginTop: 10 }}>
                  {selected.channel === 'telegram' ? 'Telegram' : 'X'} can't be sent automatically for cold outreach — {selected.channel === 'telegram' ? "a bot can't message someone who hasn't messaged it first" : "automating unsolicited DMs risks the sending account"}. Copy the message and send it yourself below.
                </Typography>
              ) : null}

              <Box style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  onClick={handleSaveEdit}
                  disabled={!isDraft || saving}
                  style={{ color: '#cfd6ff', borderColor: 'rgba(255,255,255,0.24)' }}
                >
                  Save Edit
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleCopy}
                  style={{ color: '#cfd6ff', borderColor: 'rgba(255,255,255,0.24)' }}
                >
                  Copy Message
                </Button>
                {buildComposeLink(selected) ? (
                  <Button
                    variant="outlined"
                    component="a"
                    href={buildComposeLink(selected)}
                    target={selected.channel === 'email' ? undefined : '_blank'}
                    rel="noopener noreferrer"
                    style={{ color: '#cfd6ff', borderColor: 'rgba(255,255,255,0.24)' }}
                  >
                    {composeLinkLabel(selected.channel)}
                  </Button>
                ) : null}
                <Button
                  variant="contained"
                  onClick={handleSend}
                  disabled={!isDraft || sending}
                  style={{ background: '#4d79ff', color: '#fff', fontWeight: 700 }}
                >
                  {selected.channel === 'email' ? 'Send' : 'Attempt Send (will report manual-only)'}
                </Button>
              </Box>
              {saveError ? <Typography className={classes.error}>{saveError}</Typography> : null}
              {saveStatus ? <Typography className={classes.success}>{saveStatus}</Typography> : null}
              {copyStatus ? <Typography className={classes.success}>{copyStatus}</Typography> : null}
              {sendError ? <Typography className={classes.error}>{sendError}</Typography> : null}
              {sendStatus ? <Typography className={classes.success}>{sendStatus}</Typography> : null}
            </Box>

            <Box className={classes.section}>
              <Typography className={classes.sectionLabel}>Opt-Out</Typography>
              <Typography className={classes.muted}>
                Emails carry a one-click unsubscribe link automatically. Use this if the recipient opted out some other way (e.g. replied in text) — it suppresses this contact from all future outreach immediately.
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                label="Reason (optional)"
                value={suppressReason}
                onChange={(event) => setSuppressReason(event.target.value)}
                className={classes.textField}
                style={{ marginTop: 12, marginBottom: 12 }}
              />
              <Button
                variant="outlined"
                onClick={handleSuppressContact}
                disabled={suppressing || !selected.recipientValueEncrypted}
                style={{ color: '#ff9a9a', borderColor: 'rgba(255,154,154,0.4)' }}
              >
                {suppressing ? 'Suppressing…' : 'Suppress Contact'}
              </Button>
              {suppressError ? <Typography className={classes.error}>{suppressError}</Typography> : null}
              {suppressStatus ? <Typography className={classes.success}>{suppressStatus}</Typography> : null}
            </Box>
          </>
        ) : null}

        {!detailState.loading && !selected && !detailState.error ? (
          <Typography className={classes.muted} style={{ marginTop: 18 }}>
            Select a message from the strip above.
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}
